/**
 * Google Tag Manager Configuration
 * Centralised configuration for GTM and marketing pixel tracking
 */

export const GTM_CONFIG = {
  // GTM Container ID (primary tracking method)
  gtmContainerId: 'GTM-W8974RVT',
  
  // Marketing platform compatibility
  platforms: {
    facebook: true,
    google_ads: true,
    google_analytics: true,
    linkedin: true,
    twitter: true,
  },
  
  // Cookie configuration for cross-browser compatibility
  cookieConfig: {
    cookie_flags: 'SameSite=None;Secure',
    cookie_domain: 'auto',
    cookie_expires: 63072000, // 2 years in seconds
    cookie_prefix: 'gliderol_',
  },
  
  // Privacy settings
  privacyConfig: {
    anonymize_ip: true,
    allow_google_signals: true,
    allow_ad_personalization_signals: false,
  },
  
  // Enhanced measurement settings
  enhancedMeasurement: {
    page_views: true,
    scrolls: true,
    outbound_clicks: true,
    site_search: true,
    video_engagement: true,
    file_downloads: true,
  },
  
  // Custom dimensions (if needed)
  customDimensions: {
    user_type: 'dimension1',
    door_style: 'dimension2',
    door_series: 'dimension3',
    browser_type: 'dimension4',
  },
  
  // Debug settings
  debug: {
    enabled: process.env.NODE_ENV === 'development',
    logEvents: true,
    validateEvents: true,
  },
  
  // Browser-specific settings
  browserSettings: {
    safari: {
      // Safari-specific workarounds
      useBeacon: true,
      disableThirdPartyCookies: false,
    },
    chrome: {
      // Chrome-specific settings
      enableLinkTracking: true,
    },
  },
  
  // Event configuration
  events: {
    // Page view events
    page_view: {
      send_page_view: true,
      params: ['page_title', 'page_location', 'page_path'],
    },
    
    // Custom events for the visualiser
    visualiser_start: {
      category: 'Visualiser',
      params: ['source', 'device_type'],
    },
    
    visualiser_save_option: {
      category: 'Visualiser',
      params: ['door_style', 'series', 'design', 'colour', 'panel_style'],
    },
    
    visualiser_get_quote: {
      category: 'Conversion',
      params: ['door_configuration', 'user_email'],
    },
    
    form_submit: {
      category: 'Engagement',
      params: ['form_name', 'form_destination'],
    },
    
    error: {
      category: 'Technical',
      params: ['error_type', 'error_message', 'page_location'],
    },
  },
};

// Helper function to get browser-safe configuration for GTM
export const getBrowserSafeConfig = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
  const isChrome = /chrome/.test(userAgent) && !/edg/.test(userAgent);
  const isFirefox = /firefox/.test(userAgent);
  
  const config = {
    ...GTM_CONFIG.cookieConfig,
    ...GTM_CONFIG.privacyConfig,
    browser: {
      name: isChrome ? 'chrome' : isSafari ? 'safari' : isFirefox ? 'firefox' : 'other',
      isSafari,
      isChrome,
      isFirefox
    }
  };
  
  // Safari-specific adjustments for GTM
  if (isSafari) {
    config.storage = 'none'; // Use cookies instead of localStorage
    config.transport_type = 'beacon'; // Use sendBeacon API
    config.safari_workarounds = true;
  }
  
  // Chrome-specific adjustments for GTM
  if (isChrome) {
    config.client_hints = true;
    config.enhanced_measurement = true;
  }
  
  // Firefox-specific adjustments
  if (isFirefox) {
    config.firefox_optimizations = true;
  }
  
  return config;
};

// Helper function to check if GTM is loaded
export const isGTMLoaded = () => {
  return typeof window !== 'undefined' && 
         window.google_tag_manager && 
         window.google_tag_manager[GTM_CONFIG.gtmContainerId];
};

// Helper function to check if dataLayer is available
export const isDataLayerReady = () => {
  return typeof window !== 'undefined' && 
         Array.isArray(window.dataLayer);
};

// Export environment checks
export const isProduction = process.env.NODE_ENV === 'production';
export const isDevelopment = process.env.NODE_ENV === 'development';

export default GTM_CONFIG;